import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Button, LoadingSpinner } from '../components/Layout';
import { api, InventoryDrop, DropStats } from '../services/api';
import { sendToMobiPrint } from '../services/printer';

// Map workstation codes to default part type for the Log Drop form
const WS_DEFAULT_PART_TYPE: Record<string, string> = {
  SAW1: 'housing_main',
  SAW2: 'v_track',
  SAW3: 'roller_tube',
  SAW4: 'weight_bar',
  CUTTABLE: 'fabric',
};

// Part types relevant to each workstation (filter the dropdown)
const WS_PART_TYPES: Record<string, string[]> = {
  SAW1: ['housing_main', 'housing_cover'],
  SAW2: ['v_track', 'u_channel', 'keder_guide', 'screw_cover'],
  SAW3: ['roller_tube'],
  SAW4: ['weight_bar', 'bottom_bar'],
  CUTTABLE: ['fabric', 'keder'],
};

// Material categories for different label fields
const MATERIAL_CATEGORIES: Record<string, 'extrusion' | 'fabric' | 'keder'> = {
  housing_main: 'extrusion',
  v_track: 'extrusion',
  roller_tube: 'extrusion',
  weight_bar: 'extrusion',
  u_channel: 'extrusion',
  bottom_bar: 'extrusion',
  fabric: 'fabric',
  keder: 'keder',
};

// Part types for all drop categories
const PART_TYPES = [
  { value: 'housing_main', label: 'Housing Main', category: 'extrusion' },
  { value: 'v_track', label: 'V-Track', category: 'extrusion' },
  { value: 'roller_tube', label: 'Roller Tube', category: 'extrusion' },
  { value: 'weight_bar', label: 'Weight Bar', category: 'extrusion' },
  { value: 'u_channel', label: 'U-Channel', category: 'extrusion' },
  { value: 'bottom_bar', label: 'Bottom Bar', category: 'extrusion' },
  { value: 'housing_cover', label: 'Housing Cover', category: 'extrusion' },
  { value: 'screw_cover', label: 'Screw Cover', category: 'extrusion' },
  { value: 'keder_guide', label: 'Keder Guide', category: 'extrusion' },
  { value: 'fabric', label: 'Fabric', category: 'fabric' },
  { value: 'keder', label: 'Keder Spline', category: 'keder' },
];

const STOCK_LENGTHS: Record<string, number> = {
  housing_main: 288,
  v_track: 288,
  roller_tube: 288,
  weight_bar: 288,
  u_channel: 288,
  bottom_bar: 288,
};

// Frame colors for extrusions (not roller tube)
const FRAME_COLORS = [
  { value: 'Black', label: 'Black' },
  { value: 'White', label: 'White' },
  { value: 'Bronze', label: 'Bronze' },
  { value: 'Tan', label: 'Tan' },
  { value: 'Mill Finish', label: 'Mill Finish' },
  { value: 'Custom', label: 'Custom Powder Coat' },
];

// Keder spline colors
const KEDER_COLORS = [
  { value: 'Black', label: 'Black' },
  { value: 'White', label: 'White' },
];

// Which part types get which color options (roller_tube and fabric get none here)
function getColorOptions(partType: string): { value: string; label: string }[] | null {
  if (!partType) return null;
  if (partType === 'roller_tube' || partType === 'fabric') return null;
  if (partType === 'keder') return KEDER_COLORS;
  return FRAME_COLORS;
}

// Fabric types
const FABRIC_TYPES = [
  { value: 'omega-3', label: 'Omega Pro 3%' },
  { value: 'omega-5', label: 'Omega Pro 5%' },
  { value: 'omega-10', label: 'Omega Pro 10%' },
  { value: 'twitchell-80', label: 'Twitchell 80' },
  { value: 'twitchell-90', label: 'Twitchell 90' },
  { value: 'twitchell-95', label: 'Twitchell 95' },
  { value: 'twitchell-nano-95', label: 'Twitchell Nano 95' },
  { value: 'twitchell-nano-97', label: 'Twitchell Nano 97' },
  { value: 'twitchell-nano-99', label: 'Twitchell Nano 99' },
  { value: 'twitchell-dimout-blackout', label: 'Twitchell Dimout' },
  { value: '502-vinyl', label: '502 Vinyl' },
  { value: 'twitchell-nano-55-insect', label: 'Twitchell Nano 55 Insect' },
  { value: 'strataglass', label: 'Strataglass' },
];

// Fabric colors by type
const FABRIC_COLORS: Record<string, { value: string; label: string }[]> = {
  'omega-3': [
    { value: 'black', label: 'Black' },
    { value: 'black-bronze', label: 'Black/Bronze' },
    { value: 'brown-taupe', label: 'Brown/Taupe' },
    { value: 'charcoal-taupe', label: 'Charcoal/Taupe' },
    { value: 'gray-black', label: 'Gray/Black' },
    { value: 'light-gray', label: 'Light Gray' },
    { value: 'white', label: 'White' },
    { value: 'white-gray', label: 'White Gray' },
    { value: 'white-linen', label: 'White/Linen' },
  ],
  'omega-5': [
    { value: 'black', label: 'Black' },
    { value: 'black-bronze', label: 'Black/Bronze' },
    { value: 'brown-taupe', label: 'Brown/Taupe' },
    { value: 'charcoal-taupe', label: 'Charcoal/Taupe' },
    { value: 'gray-black', label: 'Gray/Black' },
    { value: 'light-gray', label: 'Light Gray' },
    { value: 'white', label: 'White' },
    { value: 'white-gray', label: 'White Gray' },
    { value: 'white-linen', label: 'White/Linen' },
  ],
  'omega-10': [
    { value: 'black-bronze', label: 'Black/Bronze' },
    { value: 'brown-taupe', label: 'Brown/Taupe' },
    { value: 'charcoal-taupe', label: 'Charcoal/Taupe' },
    { value: 'gray-black', label: 'Gray/Black' },
    { value: 'light-gray', label: 'Light Gray' },
    { value: 'white', label: 'White' },
    { value: 'white-gray', label: 'White Gray' },
    { value: 'white-linen', label: 'White/Linen' },
  ],
  'twitchell-80': [{ value: 'black', label: 'Black' }],
  'twitchell-90': [{ value: 'black', label: 'Black' }],
  'twitchell-95': [{ value: 'black', label: 'Black' }],
  'twitchell-nano-95': [{ value: 'black', label: 'Black' }],
  'twitchell-nano-97': [{ value: 'black', label: 'Black' }],
  'twitchell-nano-99': [{ value: 'black', label: 'Black' }],
  'twitchell-dimout-blackout': [{ value: 'black', label: 'Black' }],
  '502-vinyl': [
    { value: 'black', label: 'Black' },
    { value: 'red-poppy', label: 'Red (Poppy)' },
    { value: 'white', label: 'White' },
    { value: 'blue-midnight', label: 'Blue (Midnight)' },
    { value: 'anthracite', label: 'Anthracite' },
    { value: 'grey-concrete', label: 'Grey (Concrete)' },
    { value: 'boulder', label: 'Boulder' },
    { value: 'antelope', label: 'Antelope' },
    { value: 'tan-pepper', label: 'Tan (Pepper)' },
    { value: 'hemp', label: 'Hemp' },
  ],
  'twitchell-nano-55-insect': [{ value: 'black', label: 'Black' }, { value: 'gray', label: 'Gray' }],
  'strataglass': [{ value: 'clear', label: 'Clear' }],
};

type TabType = 'log' | 'available' | 'find' | 'repair';

const REPAIR_REASONS = [
  { value: 'scratches', label: 'Scratches' },
  { value: 'dents', label: 'Dents' },
  { value: 'powder_coat_damage', label: 'Powder Coat Damage' },
  { value: 'bent', label: 'Bent/Warped' },
  { value: 'other', label: 'Other' },
];

export function DropsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const scannedDropId = searchParams.get('dropId');
  const fromWorkcard = searchParams.get('from') === 'workcard';
  const wsCode = searchParams.get('ws') || '';
  const contextOrderNumber = searchParams.get('orderNumber') || '';
  const returnWs = searchParams.get('returnWs') || '';
  const [activeTab, setActiveTab] = useState<TabType>(scannedDropId ? 'find' : 'log');
  const [stats, setStats] = useState<DropStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Build return URL for navigating back to work card (WorkCardPage reads `ws` param)
  const returnUrl = returnWs ? `/work-cards?ws=${returnWs}` : '/work-cards';

  useEffect(() => {
    loadStats();
  }, []);

  // If a dropId was passed via URL (from scanner), switch to find tab
  useEffect(() => {
    if (scannedDropId) {
      setActiveTab('find');
      // Clear the query param so re-renders don't keep triggering
      setSearchParams({}, { replace: true });
    }
  }, [scannedDropId]);

  const loadStats = async () => {
    try {
      const data = await api.getDropStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load drop stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Workstation label for display
  const wsLabel = wsCode ? wsCode.replace(/(\d)/, ' $1') : '';

  return (
    <div className="space-y-6">
      {/* Header with back button when from work card */}
      {fromWorkcard ? (
        <div>
          <button
            onClick={() => navigate(returnUrl)}
            className="flex items-center gap-2 text-teal-400 hover:text-teal-300 mb-2 text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Work Card
          </button>
          <h1 className="text-2xl font-bold text-white">Label Drop — {wsLabel}</h1>
          {contextOrderNumber && (
            <p className="text-gray-400 mt-1">Order {contextOrderNumber}</p>
          )}
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold text-white">Drops Management</h1>
          <p className="text-gray-400 mt-1">Log cut-offs and find available drops</p>
        </div>
      )}

      {/* Stats Summary — hide when coming from work card (not relevant) */}
      {!fromWorkcard && stats && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="text-center">
            <p className="text-2xl font-bold text-green-400">{stats.availableDrops}</p>
            <p className="text-xs text-gray-400">Available</p>
          </Card>
          <Card className="text-center">
            <p className="text-2xl font-bold text-yellow-400">{Number(stats.totalAvailableLength || 0).toFixed(0)}"</p>
            <p className="text-xs text-gray-400">Total Length</p>
          </Card>
          <Card className="text-center">
            <p className="text-2xl font-bold text-orange-400">{stats.repairDrops || 0}</p>
            <p className="text-xs text-gray-400">In Repair</p>
          </Card>
        </div>
      )}

      {/* Tabs — hide when from work card (go straight to log) */}
      {!fromWorkcard && (
        <div className="flex gap-2 border-b border-gray-700 pb-2">
          <button
            onClick={() => setActiveTab('log')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === 'log'
                ? 'bg-yellow-500 text-gray-900'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Log Drop
          </button>
          <button
            onClick={() => setActiveTab('available')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === 'available'
                ? 'bg-yellow-500 text-gray-900'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Available
          </button>
          <button
            onClick={() => setActiveTab('find')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === 'find'
                ? 'bg-yellow-500 text-gray-900'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Find/Scan
          </button>
          <button
            onClick={() => setActiveTab('repair')}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors relative ${
              activeTab === 'repair'
                ? 'bg-orange-500 text-gray-900'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Repair
            {stats && (stats.repairDrops || 0) > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {stats.repairDrops}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'log' && (
        <LogDropTab
          onDropCreated={loadStats}
          defaultPartType={WS_DEFAULT_PART_TYPE[wsCode] || ''}
          allowedPartTypes={WS_PART_TYPES[wsCode] || null}
          defaultOrderNumber={contextOrderNumber}
          fromWorkcard={fromWorkcard}
        />
      )}
      {activeTab === 'available' && <AvailableDropsTab onRefresh={loadStats} />}
      {activeTab === 'find' && <FindDropTab initialDropId={scannedDropId || undefined} />}
      {activeTab === 'repair' && <RepairTab onRefresh={loadStats} />}
    </div>
  );
}

// Log New Drop Tab
function LogDropTab({
  onDropCreated,
  defaultPartType = '',
  allowedPartTypes = null,
  defaultOrderNumber = '',
  fromWorkcard = false,
}: {
  onDropCreated: () => void;
  defaultPartType?: string;
  allowedPartTypes?: string[] | null;
  defaultOrderNumber?: string;
  fromWorkcard?: boolean;
}) {
  const [source, setSource] = useState<'cutting_leftover' | 'shipping_damage'>('cutting_leftover');
  const [partType, setPartType] = useState(defaultPartType);
  const [partNumber, setPartNumber] = useState('');
  const [length, setLength] = useState('');
  const [fabricWidth, setFabricWidth] = useState('');
  const [fabricHeight, setFabricHeight] = useState('');
  const [fabricType, setFabricType] = useState('');
  const [fabricColor, setFabricColor] = useState('');
  const [binLocation, setBinLocation] = useState('');
  const [orderNumber, setOrderNumber] = useState(defaultOrderNumber);
  const [notes, setNotes] = useState('');
  const [repairReason, setRepairReason] = useState('');
  const [repairVendor, setRepairVendor] = useState('');
  const [color, setColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastCreatedDrop, setLastCreatedDrop] = useState<InventoryDrop | null>(null);
  const [printing, setPrinting] = useState(false);

  // Filter part types to only show relevant ones for this workstation
  const visiblePartTypes = allowedPartTypes
    ? PART_TYPES.filter(pt => allowedPartTypes.includes(pt.value))
    : PART_TYPES;


  const isFabric = MATERIAL_CATEGORIES[partType] === 'fabric';
  const colorOptions = getColorOptions(partType);

  // Auto-fill part number based on type
  useEffect(() => {
    const partNumbers: Record<string, string> = {
      housing_main: '11001.0',
      housing_cover: '11001.1',
      v_track: '11002.0',
      roller_tube: '11003.0',
      weight_bar: '11004.0',
      u_channel: '11010.0',
      bottom_bar: '11005.0',
      screw_cover: '11012.0',
      keder_guide: '11011.0',
      keder: '11310',
    };
    if (partType && partNumbers[partType]) {
      setPartNumber(partNumbers[partType]);
    } else if (partType === 'fabric') {
      setPartNumber(''); // Fabric part number depends on type/color
    }
    // Reset color when part type changes
    setColor('');
  }, [partType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (isFabric) {
      // Fabric validation: need width and height
      if (!partType || !fabricWidth || !fabricHeight) {
        setError('Part type, width, and height are required for fabric');
        return;
      }
      const widthNum = parseFloat(fabricWidth);
      const heightNum = parseFloat(fabricHeight);
      if (isNaN(widthNum) || widthNum <= 0 || isNaN(heightNum) || heightNum <= 0) {
        setError('Invalid width or height');
        return;
      }

      setSaving(true);
      try {
        const drop = await api.createFabricDrop({
          partNumber: partNumber || 'FABRIC',
          fabricType: fabricType || 'unknown',
          fabricColor: fabricColor || 'unknown',
          lengthInches: widthNum,
          dropHeight: heightNum,
          originalRollWidth: 120, // Default roll width
          orderNumber: orderNumber || undefined,
          binLocation: binLocation || undefined,
          notes: notes || undefined,
        });
        setLastCreatedDrop(drop);
        setLastLabelInfo({ color: fabricColor || undefined, height: heightNum, width: widthNum });
        onDropCreated();
        if (fromWorkcard) {
          window.history.back();
          return;
        }
        setSuccess(`Fabric drop ${drop.dropId} created! (${widthNum}" W x ${heightNum}" H)`);
        resetForm();
      } catch (err: any) {
        setError(err.message || 'Failed to create fabric drop');
      } finally {
        setSaving(false);
      }
      return;
    }

    // Extrusion/keder validation
    if (!partType || !length) {
      setError('Part type and length are required');
      return;
    }

    const lengthNum = parseFloat(length);
    if (isNaN(lengthNum) || lengthNum <= 0) {
      setError('Invalid length');
      return;
    }

    // Minimum usable drop is 84" (only for cutting leftovers, extrusions)
    if (source === 'cutting_leftover' && lengthNum < 84) {
      if (!confirm(`Drop is ${lengthNum}" which is below 84" minimum. It will be marked as waste. Continue?`)) {
        return;
      }
    }

    setSaving(true);
    try {
      let drop: InventoryDrop;
      if (source === 'shipping_damage') {
        if (!repairReason) {
          setError('Repair reason is required for shipping damage items');
          setSaving(false);
          return;
        }
        drop = await api.createRepairItem({
          partType,
          partNumber: partNumber || partType,
          lengthInches: lengthNum,
          repairReason,
          repairVendor: repairVendor || undefined,
          notes: notes || undefined,
        });
      } else {
        drop = await api.createDrop({
          partType,
          partNumber: partNumber || partType,
          lengthInches: lengthNum,
          originalStockLength: STOCK_LENGTHS[partType] || 288,
          binLocation: binLocation || undefined,
          orderNumber: orderNumber || undefined,
          notes: notes || undefined,
        });
      }

      setLastCreatedDrop(drop);
      setLastLabelInfo({ color: color || undefined });
      onDropCreated();
      // If coming from work card, navigate back
      if (fromWorkcard) {
        window.history.back();
        return;
      }
      setSuccess(source === 'shipping_damage'
        ? `Repair item ${drop.dropId} created! Needs repair.`
        : `Drop ${drop.dropId} created! ${lengthNum < 84 ? '(Marked as waste)' : ''}`
      );
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to create drop');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setPartType(defaultPartType);
    setPartNumber('');
    setLength('');
    setFabricWidth('');
    setFabricHeight('');
    setFabricType('');
    setFabricColor('');
    setColor('');
    setBinLocation('');
    setOrderNumber(defaultOrderNumber);
    setNotes('');
    setRepairReason('');
    setRepairVendor('');
  };

  // Store label metadata alongside last created drop for printing
  const [lastLabelInfo, setLastLabelInfo] = useState<{
    color?: string;
    height?: number;
    width?: number;
  } | null>(null);

  const handleReprintLabel = async (drop: InventoryDrop) => {
    setPrinting(true);
    try {
      const result = await api.generateDropLabel({
        dropId: drop.dropId,
        partType: drop.partType,
        length: drop.lengthInches || 0,
        height: lastLabelInfo?.height || undefined,
        width: lastLabelInfo?.width || undefined,
        partNumber: drop.partNumber,
        binLocation: drop.binLocation || undefined,
        color: lastLabelInfo?.color || undefined,
      });
      await sendToMobiPrint(result.zpl);
    } catch (err: any) {
      alert(err.message || 'Failed to print label');
    } finally {
      setPrinting(false);
    }
  };

  // Print Label = create drop (no bin) + print label
  const handlePrintLabel = async () => {
    setPrinting(true);
    setError(null);
    setSuccess(null);

    try {
      let drop: InventoryDrop;
      const labelColor = isFabric ? fabricColor : color;

      if (isFabric) {
        const widthNum = parseFloat(fabricWidth);
        const heightNum = parseFloat(fabricHeight);
        if (!widthNum || !heightNum) { setError('Width and height required'); setPrinting(false); return; }
        drop = await api.createFabricDrop({
          partNumber: partNumber || 'FABRIC',
          fabricType: fabricType || 'unknown',
          fabricColor: fabricColor || 'unknown',
          lengthInches: widthNum,
          dropHeight: heightNum,
          originalRollWidth: 120,
          orderNumber: orderNumber || undefined,
          notes: notes || undefined,
        });
        // Print the label
        const result = await api.generateDropLabel({
          dropId: drop.dropId,
          partType: drop.partType,
          length: drop.lengthInches || 0,
          height: heightNum,
          width: widthNum,
          partNumber: drop.partNumber,
          color: labelColor || undefined,
        });
        await sendToMobiPrint(result.zpl);
      } else {
        const lengthNum = parseFloat(length);
        if (!lengthNum) { setError('Length required'); setPrinting(false); return; }
        drop = await api.createDrop({
          partType,
          partNumber: partNumber || partType,
          lengthInches: lengthNum,
          originalStockLength: STOCK_LENGTHS[partType] || 288,
          orderNumber: orderNumber || undefined,
          notes: notes || undefined,
        });
        // Print the label
        const result = await api.generateDropLabel({
          dropId: drop.dropId,
          partType: drop.partType,
          length: drop.lengthInches || 0,
          partNumber: drop.partNumber,
          color: labelColor || undefined,
        });
        await sendToMobiPrint(result.zpl);
      }

      setLastCreatedDrop(drop);
      onDropCreated();
      // If coming from work card, navigate back after printing
      if (fromWorkcard) {
        window.history.back();
        return;
      }
      setSuccess(`Drop ${drop.dropId} created & label printed!`);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to create drop / print label');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Card>
      {/* Source Toggle — hide when coming from work card (always cutting leftover) */}
      {!fromWorkcard && (
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setSource('cutting_leftover')}
            className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors ${
              source === 'cutting_leftover'
                ? 'bg-yellow-500 text-gray-900'
                : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            Cutting Leftover
          </button>
          <button
            type="button"
            onClick={() => setSource('shipping_damage')}
            className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors ${
              source === 'shipping_damage'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            Shipping Damage / Repair
          </button>
        </div>
      )}

      <h2 className="text-lg font-semibold text-white mb-4">
        {source === 'shipping_damage' ? 'Log Repair Item' : fromWorkcard ? 'Log Drop from Cut' : 'Log New Drop'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-green-500/20 border border-green-500 rounded-lg text-green-400 text-sm">
            <div>{success}</div>
            {lastCreatedDrop && (
              <button
                onClick={() => handleReprintLabel(lastCreatedDrop)}
                disabled={printing}
                className="mt-2 w-full px-4 py-3 bg-yellow-500 text-gray-900 rounded-lg font-bold text-base hover:bg-yellow-400 disabled:opacity-50"
              >
                {printing ? 'Printing...' : 'Reprint Label'}
              </button>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Part Type *</label>
          <select
            value={partType}
            onChange={e => setPartType(e.target.value)}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
            required
          >
            <option value="">Select part type...</option>
            {visiblePartTypes.map(pt => (
              <option key={pt.value} value={pt.value}>{pt.label}</option>
            ))}
          </select>
        </div>

        {/* Fabric: type, color, W x H fields */}
        {isFabric ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Fabric Type *</label>
                <select
                  value={fabricType}
                  onChange={e => {
                    setFabricType(e.target.value);
                    setFabricColor(''); // Reset color when type changes
                  }}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                >
                  <option value="">Select fabric...</option>
                  {FABRIC_TYPES.map(ft => (
                    <option key={ft.value} value={ft.value}>{ft.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Fabric Color *</label>
                <select
                  value={fabricColor}
                  onChange={e => setFabricColor(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  disabled={!fabricType}
                >
                  <option value="">Select color...</option>
                  {(FABRIC_COLORS[fabricType] || []).map(fc => (
                    <option key={fc.value} value={fc.value}>{fc.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Width (inches) *</label>
                <input
                  type="number"
                  value={fabricWidth}
                  onChange={e => setFabricWidth(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="Width"
                  step="0.25"
                  min="1"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Height (inches) *</label>
                <input
                  type="number"
                  value={fabricHeight}
                  onChange={e => setFabricHeight(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="Height"
                  step="0.25"
                  min="1"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
                  required
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Color dropdown for extrusions (not roller tube) and keder */}
            {colorOptions && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Color *</label>
                <select
                  value={color}
                  onChange={e => setColor(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  required
                >
                  <option value="">Select color...</option>
                  {colorOptions.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Length ({MATERIAL_CATEGORIES[partType] === 'keder' ? 'yards' : 'inches'}) *
              </label>
              <input
                type="number"
                value={length}
                onChange={e => setLength(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder={MATERIAL_CATEGORIES[partType] === 'keder' ? 'e.g., 10' : 'e.g., 96'}
                step="0.25"
                min="1"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
                required
              />
              {source === 'cutting_leftover' && length && parseFloat(length) < 84 && MATERIAL_CATEGORIES[partType] !== 'keder' && (
                <p className="text-yellow-400 text-sm mt-1">Below 84" - will be marked as waste</p>
              )}
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Bin Location</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={binLocation}
              onChange={e => setBinLocation(e.target.value.toUpperCase())}
              placeholder="Scan or enter bin (e.g., BIN-001)"
              className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const bin = prompt('Scan bin barcode:');
                if (bin) setBinLocation(bin.toUpperCase());
              }}
            >
              Scan
            </Button>
          </div>
        </div>

        {/* From Order — hide when pre-filled from work card */}
        {!defaultOrderNumber && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">From Order (optional)</label>
            <input
              type="text"
              value={orderNumber}
              onChange={e => setOrderNumber(e.target.value.toUpperCase())}
              placeholder="e.g., VT26010057"
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Notes (optional)</label>
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={source === 'shipping_damage' ? 'Describe the damage' : 'Any notes about this drop'}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
          />
        </div>

        {/* Repair-specific fields for shipping damage */}
        {source === 'shipping_damage' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Repair Reason *</label>
              <select
                value={repairReason}
                onChange={e => setRepairReason(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-400"
                required
              >
                <option value="">Select reason...</option>
                {REPAIR_REASONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Vendor (optional)</label>
              <input
                type="text"
                value={repairVendor}
                onChange={e => setRepairVendor(e.target.value)}
                placeholder="e.g., Local powder coat shop"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
              />
            </div>
          </>
        )}

        {/* Print Label — creates drop + prints label */}
        {source !== 'shipping_damage' && (
          <button
            type="button"
            onClick={handlePrintLabel}
            disabled={printing || !partType || (isFabric ? (!fabricWidth || !fabricHeight || !fabricType || !fabricColor) : (!length || (colorOptions && !color)))}
            className="w-full py-4 bg-yellow-500 text-gray-900 rounded-lg font-bold text-base hover:bg-yellow-400 disabled:opacity-50"
          >
            {printing ? 'Creating & Printing...' : 'Print Label'}
          </button>
        )}

        {/* Log without printing — only show when NOT from work card */}
        {!fromWorkcard && (
          <Button
            type="submit"
            variant={source === 'shipping_damage' ? 'danger' : 'secondary'}
            disabled={saving || !partType || (isFabric ? (!fabricWidth || !fabricHeight || !fabricType || !fabricColor) : (!length || (colorOptions && !color))) || (source === 'shipping_damage' && !repairReason)}
            className="w-full py-4"
          >
            {saving ? 'Saving...' : source === 'shipping_damage' ? 'Log Repair Item' : 'Log Drop (No Print)'}
          </Button>
        )}
      </form>
    </Card>
  );
}

// Available Drops Tab
function AvailableDropsTab({ onRefresh }: { onRefresh?: () => void }) {
  const [selectedType, setSelectedType] = useState('');
  const [drops, setDrops] = useState<InventoryDrop[]>([]);
  const [loading, setLoading] = useState(false);
  const [minLength, setMinLength] = useState('');

  const loadDrops = useCallback(async () => {
    if (!selectedType) return;
    setLoading(true);
    try {
      const data = await api.getAvailableDrops(
        selectedType,
        minLength ? parseFloat(minLength) : undefined
      );
      setDrops(data);
    } catch (error) {
      console.error('Failed to load drops:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedType, minLength]);

  useEffect(() => {
    if (selectedType) {
      loadDrops();
    }
  }, [selectedType, loadDrops]);

  const handleUseDrop = async (drop: InventoryDrop) => {
    const orderNumber = prompt('Enter order number to use this drop for:');
    if (!orderNumber) return;

    try {
      await api.useDrop(drop.id, {
        orderNumber: orderNumber.toUpperCase(),
      });
      alert(`Drop ${drop.dropId} marked as used for ${orderNumber.toUpperCase()}`);
      loadDrops();
      onRefresh?.();
    } catch (error: any) {
      alert(error.message || 'Failed to use drop');
    }
  };

  return (
    <Card>
      <h2 className="text-lg font-semibold text-white mb-4">Available Drops</h2>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Part Type</label>
            <select
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
            >
              <option value="">Select type...</option>
              {PART_TYPES.map(pt => (
                <option key={pt.value} value={pt.value}>{pt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Min Length</label>
            <input
              type="number"
              value={minLength}
              onChange={e => setMinLength(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="e.g., 96"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : drops.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            {selectedType ? 'No available drops for this type' : 'Select a part type to see drops'}
          </div>
        ) : (
          <div className="space-y-2">
            {drops.map(drop => (
              <AvailableDropRow
                key={drop.id}
                drop={drop}
                onUseDrop={() => handleUseDrop(drop)}
                onRepair={() => { loadDrops(); onRefresh?.(); }}
                onDeleted={() => { loadDrops(); onRefresh?.(); }}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// Available Drop Row Component with Use, Print, Delete, and Repair buttons
function AvailableDropRow({
  drop,
  onUseDrop,
  onRepair,
  onDeleted,
}: {
  drop: InventoryDrop;
  onUseDrop: () => void;
  onRepair: () => void;
  onDeleted: () => void;
}) {
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [repairReason, setRepairReason] = useState('');
  const [repairVendor, setRepairVendor] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleMarkRepair = async () => {
    if (!repairReason) return;
    setRepairing(true);
    try {
      await api.markDropNeedsRepair(drop.id, repairReason, repairVendor || undefined);
      setShowRepairModal(false);
      onRepair();
    } catch (err: any) {
      alert(err.message || 'Failed to mark as needs repair');
    } finally {
      setRepairing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete drop ${drop.dropId}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deleteDrop(drop.id);
      onDeleted();
    } catch (err: any) {
      alert(err.message || 'Failed to delete drop');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="p-3 bg-gray-700 rounded-lg flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-white font-medium">
            {drop.lengthInches}" - {drop.partType.replace(/_/g, ' ')}
          </div>
          <div className="text-sm text-gray-400">
            {drop.dropId} {drop.binLocation && `| Bin: ${drop.binLocation}`}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {drop.createdAt ? new Date(drop.createdAt).toLocaleDateString() : ''}
            {drop.createdByName ? ` · ${drop.createdByName}` : ''}
            {drop.orderNumber ? ` · Order ${drop.orderNumber}` : ''}
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="secondary" size="sm" onClick={() => setShowPrintModal(true)}>
            🏷️
          </Button>
          <button
            onClick={() => setShowRepairModal(true)}
            className="px-2 py-1 bg-orange-500/20 text-orange-400 border border-orange-500/50 rounded text-xs font-medium hover:bg-orange-500/30"
          >
            Repair
          </button>
          <Button variant="success" size="sm" onClick={onUseDrop}>
            Use
          </Button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/50 rounded text-xs font-medium hover:bg-red-500/30 disabled:opacity-50"
          >
            {deleting ? '...' : 'Del'}
          </button>
        </div>
      </div>
      {showPrintModal && (
        <PrintDropLabelModal drop={drop} onClose={() => setShowPrintModal(false)} />
      )}
      {showRepairModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-white mb-4">Mark as Needs Repair</h2>
            <div className="p-3 bg-gray-900 rounded-lg mb-4">
              <div className="text-yellow-400 font-bold">{drop.dropId}</div>
              <div className="text-white">{drop.lengthInches}" {drop.partType.replace(/_/g, ' ')}</div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Reason *</label>
                <select
                  value={repairReason}
                  onChange={e => setRepairReason(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-400"
                >
                  <option value="">Select reason...</option>
                  {REPAIR_REASONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Vendor (optional)</label>
                <input
                  type="text"
                  value={repairVendor}
                  onChange={e => setRepairVendor(e.target.value)}
                  placeholder="e.g., Local powder coat shop"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-400"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={() => setShowRepairModal(false)} className="flex-1">Cancel</Button>
              <button
                onClick={handleMarkRepair}
                disabled={!repairReason || repairing}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium disabled:opacity-50 hover:bg-orange-600"
              >
                {repairing ? 'Saving...' : 'Mark Needs Repair'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Find/Scan Drop Tab
function FindDropTab({ initialDropId }: { initialDropId?: string }) {
  const [searchInput, setSearchInput] = useState(initialDropId || '');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<InventoryDrop | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newBinLocation, setNewBinLocation] = useState('');
  const [updating, setUpdating] = useState(false);
  const [usingDrop, setUsingDrop] = useState(false);

  // Auto-search if initialDropId is provided (from barcode scanner)
  useEffect(() => {
    if (initialDropId) {
      doSearch(initialDropId);
    }
  }, [initialDropId]);

  const doSearch = async (query: string) => {
    if (!query.trim()) return;

    setSearching(true);
    setError(null);
    setResult(null);

    try {
      const drop = await api.getDropByDropId(query.trim());
      setResult(drop);
      setNewBinLocation(drop.binLocation || '');
    } catch {
      try {
        const drop = await api.getDropById(query.trim());
        setResult(drop);
        setNewBinLocation(drop.binLocation || '');
      } catch {
        setError(`Drop "${query}" not found`);
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = async () => {
    await doSearch(searchInput);
  };

  const handleUpdateLocation = async () => {
    if (!result || !newBinLocation.trim()) return;

    setUpdating(true);
    try {
      const updated = await api.updateDropLocation(result.id, newBinLocation.trim());
      setResult(updated);
      alert('Bin location updated!');
    } catch (err: any) {
      alert(err.message || 'Failed to update location');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Card>
      <h2 className="text-lg font-semibold text-white mb-4">Find Drop by ID</h2>

      <div className="space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Scan or enter drop ID (DRP-20260119-0001)"
            className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
          />
          <Button
            variant="primary"
            onClick={handleSearch}
            disabled={searching || !searchInput.trim()}
          >
            {searching ? '...' : 'Find'}
          </Button>
        </div>

        {error && (
          <div className="p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {result && (
          <div className="p-4 bg-gray-700 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-yellow-400 font-bold text-lg">{result.dropId}</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                result.status === 'available' ? 'bg-green-500/20 text-green-400' :
                result.status === 'used' ? 'bg-blue-500/20 text-blue-400' :
                result.status === 'reserved' ? 'bg-yellow-500/20 text-yellow-400' :
                result.status === 'needs_repair' ? 'bg-orange-500/20 text-orange-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {result.status === 'needs_repair' ? 'needs repair' : result.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-400">Type:</span>
                <span className="text-white ml-2">{result.partType.replace(/_/g, ' ')}</span>
              </div>
              <div>
                <span className="text-gray-400">Length:</span>
                <span className="text-white ml-2">{result.lengthInches}"</span>
              </div>
              <div>
                <span className="text-gray-400">Part #:</span>
                <span className="text-white ml-2">{result.partNumber}</span>
              </div>
              <div>
                <span className="text-gray-400">Created:</span>
                <span className="text-white ml-2">{new Date(result.createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            {result.orderNumber && (
              <div className="text-sm">
                <span className="text-gray-400">From Order:</span>
                <span className="text-white ml-2">{result.orderNumber}</span>
              </div>
            )}

            {/* Update Bin Location */}
            <div className="pt-3 border-t border-gray-600">
              <label className="block text-sm font-medium text-gray-300 mb-2">Bin Location</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newBinLocation}
                  onChange={e => setNewBinLocation(e.target.value.toUpperCase())}
                  placeholder="Enter or scan bin location"
                  className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
                />
                <Button
                  variant="success"
                  onClick={handleUpdateLocation}
                  disabled={updating || newBinLocation === (result.binLocation || '')}
                >
                  {updating ? '...' : 'Update'}
                </Button>
              </div>
            </div>

            {result.notes && (
              <div className="text-sm text-gray-400">
                Notes: {result.notes}
              </div>
            )}

            {/* Actions for available drops */}
            {result.status === 'available' && (
              <div className="pt-3 border-t border-gray-600 space-y-2">
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      const orderNumber = prompt('Enter order number to use this drop for:');
                      if (!orderNumber) return;
                      setUsingDrop(true);
                      try {
                        await api.useDrop(result.id, { orderNumber: orderNumber.toUpperCase() });
                        const updated = await api.getDropById(result.id);
                        setResult(updated);
                      } catch (err: any) {
                        setError(err.message || 'Failed to use drop');
                      } finally {
                        setUsingDrop(false);
                      }
                    }}
                    disabled={usingDrop}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    {usingDrop ? 'Marking...' : 'Use for Order'}
                  </button>
                  {result.binLocation && (
                    <button
                      onClick={async () => {
                        if (!confirm(`Remove ${result.dropId} from bin ${result.binLocation}?`)) return;
                        try {
                          const updated = await api.updateDropLocation(result.id, '');
                          setResult(updated);
                          setNewBinLocation('');
                        } catch (err: any) {
                          setError(err.message || 'Failed to clear bin');
                        }
                      }}
                      className="px-4 py-3 bg-red-600/20 text-red-400 border border-red-500/50 rounded-lg font-medium hover:bg-red-600/30"
                    >
                      Remove from Bin
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Delete button for available/waste drops (not repairs or used) */}
            {(result.status === 'available' || result.status === 'waste') && (
              <div className="pt-3 border-t border-gray-600">
                <button
                  onClick={async () => {
                    if (!confirm(`Permanently delete drop ${result.dropId}? This cannot be undone.`)) return;
                    try {
                      await api.deleteDrop(result.id);
                      setResult(null);
                      setSearchInput('');
                    } catch (err: any) {
                      setError(err.message || 'Failed to delete drop');
                    }
                  }}
                  className="w-full px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
                >
                  Delete Drop
                </button>
              </div>
            )}

            {/* Status info for non-available drops */}
            {result.status === 'used' && result.usedInOrderNumber && (
              <div className="pt-3 border-t border-gray-600">
                <div className="p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg text-sm text-blue-300">
                  Used in order <span className="font-bold">{result.usedInOrderNumber}</span>
                  {result.usedAt && ` on ${new Date(result.usedAt).toLocaleDateString()}`}
                </div>
              </div>
            )}

            {/* Print Label Button */}
            <div className="pt-3 border-t border-gray-600">
              <PrintDropLabelButton drop={result} />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// Repair Tab - shows drops needing repair with selectable batch print for powder coating
function RepairTab({ onRefresh }: { onRefresh?: () => void }) {
  const [drops, setDrops] = useState<InventoryDrop[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [vendorInput, setVendorInput] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadRepairDrops();
  }, []);

  const loadRepairDrops = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${(window as any).__API_URL || '/api'}/inventory/drops?status=needs_repair`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDrops(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to load repair drops:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkSent = async (drop: InventoryDrop) => {
    const vendor = vendorInput[drop.id]?.trim();
    if (!vendor) {
      alert('Please enter a vendor name');
      return;
    }
    setActionLoading(drop.id);
    try {
      await api.markDropRepairSent(drop.id, vendor);
      await loadRepairDrops();
      onRefresh?.();
    } catch (err: any) {
      alert(err.message || 'Failed to mark as sent');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRepairComplete = async (drop: InventoryDrop) => {
    if (!confirm(`Mark ${drop.dropId} as repair complete? It will be returned to available inventory.`)) return;
    setActionLoading(drop.id);
    try {
      await api.markDropRepairComplete(drop.id);
      await loadRepairDrops();
      onRefresh?.();
    } catch (err: any) {
      alert(err.message || 'Failed to complete repair');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === drops.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(drops.map(d => d.id)));
    }
  };

  const handlePrintBatch = () => {
    const selected = drops.filter(d => selectedIds.has(d.id));
    if (selected.length === 0) { alert('Select items to print'); return; }

    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Group by part type for organized BOL
    const byType: Record<string, InventoryDrop[]> = {};
    for (const d of selected) {
      const key = d.partType.replace(/_/g, ' ');
      if (!byType[key]) byType[key] = [];
      byType[key].push(d);
    }

    const rows = selected.map((d, i) => `
      <tr>
        <td style="padding:8px;border:1px solid #ccc;text-align:center;">${i + 1}</td>
        <td style="padding:8px;border:1px solid #ccc;">${d.dropId}</td>
        <td style="padding:8px;border:1px solid #ccc;">${d.partType.replace(/_/g, ' ')}</td>
        <td style="padding:8px;border:1px solid #ccc;">${d.partNumber}</td>
        <td style="padding:8px;border:1px solid #ccc;text-align:right;">${d.lengthInches}"</td>
        <td style="padding:8px;border:1px solid #ccc;">${(d.repairReason || '').replace(/_/g, ' ')}</td>
        <td style="padding:8px;border:1px solid #ccc;">${d.repairVendor || '—'}</td>
      </tr>
    `).join('');

    const totalLength = selected.reduce((sum, d) => sum + d.lengthInches, 0);

    const html = `<!DOCTYPE html>
<html><head><title>Powder Coating BOL - ${today}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 40px; color: #000; }
  h1 { font-size: 24px; margin-bottom: 4px; }
  h2 { font-size: 16px; color: #555; font-weight: normal; margin-top: 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th { background: #f0f0f0; padding: 10px 8px; border: 1px solid #ccc; text-align: left; font-size: 13px; }
  td { font-size: 13px; }
  .summary { margin-top: 20px; padding: 12px; background: #f9f9f9; border: 1px solid #ddd; border-radius: 4px; }
  .summary strong { font-size: 15px; }
  .footer { margin-top: 40px; display: flex; justify-content: space-between; }
  .sig-line { border-top: 1px solid #000; width: 200px; margin-top: 40px; padding-top: 4px; font-size: 12px; color: #666; }
  @media print { body { margin: 20px; } }
</style></head><body>
  <h1>Powder Coating Bill of Lading</h1>
  <h2>Keder Screens — ${today}</h2>
  <table>
    <thead>
      <tr>
        <th style="width:40px;">#</th>
        <th>Drop ID</th>
        <th>Part Type</th>
        <th>Part #</th>
        <th style="text-align:right;">Length</th>
        <th>Repair Reason</th>
        <th>Vendor</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="summary">
    <strong>Total Items: ${selected.length}</strong> &nbsp;|&nbsp;
    <strong>Total Length: ${totalLength.toFixed(1)}"</strong> (${(totalLength / 12).toFixed(1)} ft)
  </div>
  <div class="footer">
    <div class="sig-line">Sent by (signature)</div>
    <div class="sig-line">Date sent</div>
    <div class="sig-line">Received by (signature)</div>
  </div>
  <script>window.print();</script>
</body></html>`;

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  if (loading) {
    return <Card className="text-center py-8"><LoadingSpinner /></Card>;
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Items Needing Repair</h2>
        {drops.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={toggleAll}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-600 text-gray-300 hover:bg-gray-500"
            >
              {selectedIds.size === drops.length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={handlePrintBatch}
              disabled={selectedIds.size === 0}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Print Batch ({selectedIds.size})
            </button>
          </div>
        )}
      </div>

      {drops.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No items currently needing repair
        </div>
      ) : (
        <div className="space-y-3">
          {drops.map(drop => (
            <div key={drop.id} className={`p-4 bg-gray-700 rounded-lg border-l-4 ${selectedIds.has(drop.id) ? 'border-l-blue-500 ring-1 ring-blue-500/50' : 'border-l-orange-500'}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(drop.id)}
                    onChange={() => toggleSelect(drop.id)}
                    className="mt-1 w-5 h-5 rounded accent-blue-500"
                  />
                  <div>
                    <div className="text-yellow-400 font-bold">{drop.dropId}</div>
                    <div className="text-white font-medium">{drop.lengthInches}" {drop.partType.replace(/_/g, ' ')}</div>
                    <div className="text-sm text-gray-400">{drop.partNumber}</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    drop.source === 'shipping_damage'
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-orange-500/20 text-orange-400'
                  }`}>
                    {drop.source === 'shipping_damage' ? 'SHIPPING DMG' : 'DROP REPAIR'}
                  </span>
                </div>
              </div>

              {/* Repair details */}
              <div className="text-sm space-y-1 mb-3 ml-8">
                {drop.repairReason && (
                  <div><span className="text-gray-500">Reason:</span> <span className="text-orange-300">{drop.repairReason.replace(/_/g, ' ')}</span></div>
                )}
                {drop.repairVendor && (
                  <div><span className="text-gray-500">Vendor:</span> <span className="text-white">{drop.repairVendor}</span></div>
                )}
                {drop.repairSentAt && (
                  <div><span className="text-gray-500">Sent:</span> <span className="text-white">{new Date(drop.repairSentAt).toLocaleDateString()}</span></div>
                )}
                {drop.binLocation && (
                  <div><span className="text-gray-500">Bin:</span> <span className="text-white">{drop.binLocation}</span></div>
                )}
              </div>

              {/* Actions — NO delete for repair items */}
              <div className="flex gap-2 ml-8">
                {!drop.repairSentAt && (
                  <div className="flex gap-2 flex-1">
                    <input
                      type="text"
                      value={vendorInput[drop.id] || drop.repairVendor || ''}
                      onChange={e => setVendorInput(prev => ({ ...prev, [drop.id]: e.target.value }))}
                      placeholder="Vendor name..."
                      className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white placeholder-gray-400 text-sm focus:outline-none focus:border-orange-400"
                    />
                    <button
                      onClick={() => handleMarkSent(drop)}
                      disabled={actionLoading === drop.id}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {actionLoading === drop.id ? '...' : 'Mark Sent'}
                    </button>
                  </div>
                )}
                <button
                  onClick={() => handleRepairComplete(drop)}
                  disabled={actionLoading === drop.id}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {actionLoading === drop.id ? '...' : 'Repair Complete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// Print Drop Label Button Component
function PrintDropLabelButton({ drop }: { drop: InventoryDrop }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => setShowModal(true)}
        className="w-full"
      >
        🏷️ Print Label
      </Button>
      {showModal && (
        <PrintDropLabelModal
          drop={drop}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

// Print Drop Label Modal with material-specific fields
function PrintDropLabelModal({
  drop,
  onClose,
}: {
  drop: InventoryDrop;
  onClose: () => void;
}) {
  const materialType = MATERIAL_CATEGORIES[drop.partType] || 'extrusion';
  const modalColorOptions = getColorOptions(drop.partType);

  // Dimension fields - pre-fill from drop if available
  const [length, setLength] = useState(drop.lengthInches?.toString() || '');
  const [height, setHeight] = useState(drop.dropHeight?.toString() || '');
  const [width, setWidth] = useState('');
  const [labelColor, setLabelColor] = useState((drop as any).fabricColor || '');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const handlePrint = async () => {
    setGenerating(true);
    setError('');

    try {
      const result = await api.generateDropLabel({
        dropId: drop.dropId,
        partType: drop.partType,
        length: parseFloat(length) || 0,
        height: materialType === 'fabric' ? parseFloat(height) || undefined : undefined,
        width: materialType === 'fabric' ? parseFloat(width) || undefined : undefined,
        partNumber: drop.partNumber,
        binLocation: drop.binLocation,
        color: labelColor || undefined,
      });

      // Download .zpl file — Zebra app opens it and prints
      await sendToMobiPrint(result.zpl);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to generate label');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Print Drop Label</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Drop Info */}
          <div className="p-3 bg-gray-700 rounded-lg">
            <div className="text-yellow-400 font-bold">{drop.dropId}</div>
            <div className="text-sm text-gray-300">{drop.partType.replace(/_/g, ' ')} | {drop.partNumber}</div>
            {drop.binLocation && <div className="text-sm text-gray-400">Bin: {drop.binLocation}</div>}
          </div>

          {/* Color selector for extrusions and keder */}
          {modalColorOptions && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Color</label>
              <select
                value={labelColor}
                onChange={e => setLabelColor(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              >
                <option value="">Select color...</option>
                {modalColorOptions.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Material-specific dimension fields */}
          {materialType === 'extrusion' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Length (inches) *</label>
              <input
                type="number"
                value={length}
                onChange={e => setLength(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                step="0.125"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              />
            </div>
          )}

          {materialType === 'fabric' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Height (inches) *</label>
                  <input
                    type="number"
                    value={height}
                    onChange={e => setHeight(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    step="0.125"
                    placeholder="Drop height"
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Width (inches)</label>
                  <input
                    type="number"
                    value={width}
                    onChange={e => setWidth(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    step="0.125"
                    placeholder="Cut width"
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Length (roll length)</label>
                <input
                  type="number"
                  value={length}
                  onChange={e => setLength(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  step="0.125"
                  placeholder="Remaining roll length"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                />
              </div>
            </>
          )}

          {materialType === 'keder' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Length (yards) *</label>
              <input
                type="number"
                value={length}
                onChange={e => setLength(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                step="0.25"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              />
              <p className="text-xs text-gray-500 mt-1">Enter length in yards for keder spline</p>
            </div>
          )}

          <Button
            variant="primary"
            onClick={handlePrint}
            disabled={generating || !length}
            className="w-full py-4"
          >
            {generating ? 'Generating...' : '🖨️ Generate Label'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DropsPage;
